// src/features/engine/runners/TaskExecutor.jsx

import { useCallback, useEffect, useState, useRef } from "react";
import axios from "axios";

import { useAuth } from "../../../auth/authContext";
import {
  ApplicationButton,
  ApplicationStateRegion,
  ApplicationSurface
} from "../../../components/application";
import { useProgramEngine } from "../context/ProgramEngineContext";
import { TaskRuntimeProvider } from "../context/TaskRuntimeContext";

import useEvaluationSession from "../../evaluation/hooks/useEvaluationSession";
import TaskRenderer from "../components/TaskRenderer";
import CompletedProgramStepReview from "../review/CompletedProgramStepReview";
import {
  readResolvedStepDenialPresentation,
  readResolvedStepState,
} from "../utils/resolvedStepState";

export default function TaskExecutor({ step }) {

  const auth = useAuth();

  const {
    program,
    attempt,
    completeCurrentStep,
    navigateToProgramDashboard,
    navigateToProgramStep,
    projectSlug,
    programState,
    programScopedLocale,
    refetchProgramState
  } = useProgramEngine();

  const apiUrl =
    process.env.REACT_APP_API_URL ||
    "http://localhost:5001/api";

  const [task, setTask] = useState(null);
  const [loadingTask, setLoadingTask] = useState(true);
  const [completedReview, setCompletedReview] = useState(null);
  const [reviewError, setReviewError] = useState("");

  const hasStartedSession = useRef(false);

  const {
    session,
    responses,
    loading: loadingSession,
    error: sessionError,
    startSession
  } = useEvaluationSession({
    token: auth.user?.token,
    apiUrl
  });

  // =====================================================
  // 🔥 STRICT STEP
  // =====================================================

  const effectiveStepKey = step?.key;

  // =====================================================
  // PROGRESS
  // =====================================================

  const {
    completed: isCompleted,
    eligible: isEligible,
    repeatable: isRepeatable,
    unlocked: isUnlocked
  } = readResolvedStepState(step);
  const denialPresentation = readResolvedStepDenialPresentation(step);
  const reviewMode = isCompleted &&
    step?.resolvedExecutionPolicy?.completedEntry?.mode === "review";

  // =====================================================
  // FETCH TASK
  // =====================================================

  useEffect(() => {

    if (!program || !effectiveStepKey || !isEligible || reviewMode) return;

    async function fetchTask() {

      try {

        const res = await axios.get(
          `${apiUrl}/evaluation/task/by-program-step`,
          {
            params: {
              programSlug: program.programSlug,
              stepKey: effectiveStepKey
            },
            headers: {
              Authorization: `Bearer ${auth.user?.token}`
            }
          }
        );

        setTask(res.data);

      } catch (err) {
        console.error("Task fetch error:", err);
      } finally {
        setLoadingTask(false);
      }

    }

    fetchTask();

  }, [program, effectiveStepKey, isEligible, reviewMode, apiUrl, auth.user?.token]);

  useEffect(() => {
    if (!reviewMode || !program || !attempt?._id || !attempt?.projectId) return;
    let active = true;
    setLoadingTask(true);
    setReviewError("");
    axios.get(`${apiUrl}/evaluation/program-step/completed-review`, {
      params: {
        programSlug: program.programSlug,
        programStepKey: effectiveStepKey,
        programAttemptId: attempt._id,
        projectId: attempt.projectId,
      },
      headers: { Authorization: `Bearer ${auth.user?.token}` },
    }).then(result => {
      if (active) setCompletedReview(result.data.review);
    }).catch(() => {
      if (active) setReviewError("This completed-session review is unavailable.");
    }).finally(() => {
      if (active) setLoadingTask(false);
    });
    return () => { active = false; };
  }, [apiUrl, attempt?._id, attempt?.projectId, auth.user?.token, effectiveStepKey, program, reviewMode]);

  // =====================================================
  // START SESSION
  // =====================================================

  const useCase = step?.useCase || "classroom";

  const startCurrentSession = useCallback(() => startSession({
    useCase,
    taskId: task?._id,
    programId: program?._id,
    programStepKey: effectiveStepKey,
    programAttemptId: attempt?._id,
    projectId: attempt?.projectId
  }), [
    attempt?._id,
    attempt?.projectId,
    effectiveStepKey,
    program?._id,
    startSession,
    task?._id,
    useCase
  ]);

  useEffect(() => {

    if (reviewMode || !task || !program || !effectiveStepKey || !isEligible) return;
    if (!attempt?.projectId) return;
    if (session) return;
    if (hasStartedSession.current) return;

    hasStartedSession.current = true;

    startCurrentSession();

  }, [
    task,
    program,
    session,
    startCurrentSession,
    attempt,
    effectiveStepKey,
    isEligible,
    useCase, // 👈 ARA SÍ, NET
    reviewMode,
  ]);

  // =====================================================
  // GUARDS
  // =====================================================

  if (!step || !step.key) {
    console.error("❌ TaskExecutor: missing step", step);
    return <div>Error: invalid step</div>;
  }

  if (!isUnlocked) {
    return (
      <div className="app-module">
        <h3>🔒 Step locked</h3>
      </div>
    );
  }

  if (isCompleted && !isRepeatable) {
    return (
      <div className="app-module">
        <h3>✅ Step already completed</h3>
      </div>
    );
  }

  if (reviewMode) {
    if (reviewError) return <ApplicationSurface aria-label="Completed session review unavailable"><ApplicationStateRegion message={reviewError} status="error" /></ApplicationSurface>;
    if (loadingTask || !completedReview) return <div>Loading review…</div>;
    return (
      <div className="program-step-execution" data-content-canvas="interactive-wide" role="region" aria-label="Completed program activity review">
        <CompletedProgramStepReview review={completedReview} program={program} step={step} onDone={navigateToProgramDashboard} />
      </div>
    );
  }

  if (!isEligible) {
    return (
      <div className="app-module">
        <h3>🔒 {denialPresentation.title}</h3>
      </div>
    );
  }

  if (sessionError) {
    return (
      <ApplicationSurface aria-label="Evaluation session start error">
        <ApplicationStateRegion
          message={sessionError}
          status="error"
        />
        <ApplicationButton
          disabled={loadingSession}
          onClick={startCurrentSession}
        >
          Retry
        </ApplicationButton>
      </ApplicationSurface>
    );
  }

  if (loadingTask || loadingSession || !session) {
    return <div>Loading...</div>;
  }

  const backPath =
    programScopedLocale?.isEnabled && projectSlug
      ? `/app/project/${projectSlug}${programScopedLocale.buildSearchWithLocale({
        step: null
      })}`
      : undefined;

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <TaskRuntimeProvider
      session={session}
      step={step}
      program={program}
      task={task}
      responses={responses}
      apiUrl={apiUrl}
      token={auth.user?.token}
      completeCurrentStep={completeCurrentStep}
      navigateToProgramDashboard={navigateToProgramDashboard}
      navigateToProgramStep={navigateToProgramStep}
      refetchProgramState={refetchProgramState}
      backPath={backPath}
      programScopedLocale={programScopedLocale}
      temporalDelivery={programState?.temporalDelivery}
      participantTimeZone={programState?.learnerPresentation?.participantTimeZone}
    >
      <div className="program-step-execution" data-content-canvas="interactive-wide" role="region" aria-label="Program activity">
        <TaskRenderer step={step} />
      </div>
    </TaskRuntimeProvider>
  );
}
